import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import saleService from "../../../Services/saleService";
import labService from "../../../Services/labtestservices";

const Income = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const [searchData, setSearchData] = useState("");
  const [medicinesGroupedData, setMedicinesGroupedData] = useState([]);
  const [servicesGroupedData, setServicesGroupedData] = useState([]);
  const [patientGroupedData, setPatientGroupedData] = useState([]);
  const [radiologyGroupedData, setRadiologyGroupedData] = useState([]);
  const [laboratoryGroupedData, setLaboratoryGroupedData] = useState([]);
  const [paginatedMedicinesData, setPaginatedMedicinesData] = useState([]);
  const [paginatedServicesData, setPaginatedServicesData] = useState([]);
  const [paginatedPatientData, setPaginatedPatientData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  /* ---------- helpers ---------- */

  const formatDate = (date) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return date instanceof Date
      ? date.toLocaleDateString("en-US", options)
      : new Date(date).toLocaleDateString("en-US", options);
  };

  const getAllDatesInRange = () => {
    const today = new Date();
    const allDates = [];
    for (let i = 0; i <= 31; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      allDates.push(formatDate(date));
    }
    return allDates;
  };

  const groupByDate = (sales) => {
    const dateMap = {};
    const allDates = getAllDatesInRange();

    sales.forEach((sale) => {
      const { invoice, unit_price, quantity, sale_date } = sale;
      const total = quantity * unit_price;
      const dateKey = formatDate(sale_date);

      if (!dateMap[dateKey]) {
        dateMap[dateKey] = { date: dateKey, totalAmount: 0, invoices: new Set() };
      }
      dateMap[dateKey].totalAmount += total;
      dateMap[dateKey].invoices.add(invoice);
    });

    allDates.forEach((date) => {
      if (!dateMap[date]) {
        dateMap[date] = { date, totalAmount: 0, invoices: new Set() };
      }
    });

    return Object.values(dateMap)
      .map((group) => ({ ...group, totalInvoiceCount: group.invoices.size }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const groupByPatientDate = (sales) => {
    const dateMap = {};
    const allDates = getAllDatesInRange();

    sales.forEach((sale) => {
      const { fee, date } = sale;
      const dateKey = formatDate(date);

      if (!dateMap[dateKey]) {
        dateMap[dateKey] = { date: dateKey, totalAmount: 0, patientCount: 0 };
      }
      dateMap[dateKey].totalAmount += Number(fee) || 0;
      dateMap[dateKey].patientCount += 1;
    });

    allDates.forEach((date) => {
      if (!dateMap[date]) {
        dateMap[date] = { date, totalAmount: 0, patientCount: 0 };
      }
    });

    return Object.values(dateMap).sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Lab records ko type (radiology / laboratory) ke hisaab se date-wise group karta hai
  const groupByExamType = (records, typeName) => {
    const dateMap = {};
    const allDates = getAllDatesInRange();

    records.forEach((r) => {
      const type = String(r.type ?? r.category ?? r.test_type ?? "").toLowerCase();
      if (!type.includes(typeName)) return;

      const rawDate = r.date ?? r.order_date ?? r.created_at ?? r.createdAt;
      if (!rawDate) return;

      const dateKey = formatDate(rawDate);
      if (!dateMap[dateKey]) {
        dateMap[dateKey] = { date: dateKey, totalAmount: 0, orderCount: 0 };
      }
      dateMap[dateKey].orderCount += 1;
      dateMap[dateKey].totalAmount +=
        Number(r.amount ?? r.price ?? r.fee ?? r.cost ?? 0) || 0;
    });

    allDates.forEach((date) => {
      if (!dateMap[date]) dateMap[date] = { date, totalAmount: 0, orderCount: 0 };
    });

    return Object.values(dateMap);
  };

  /* ---------- data fetching ---------- */

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await saleService.fetchAllMedicinesSales();
        setMedicinesGroupedData(groupByDate(response.sales || []));
      } catch (error) {
        toast.error("Error fetching medicines sales data");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await saleService.fetchAllServicesSale();
        setServicesGroupedData(groupByDate(response.sales || []));
      } catch (error) {
        toast.error("Error fetching services sales data");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await saleService.fetchAllPSales();
        setPatientGroupedData(groupByPatientDate(response.sales || []));
      } catch (error) {
        toast.error("Error fetching patient sales data");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchLabData = async () => {
      try {
        const response = await labService.fetchAll();
        console.log("lab response:", response); // shape check karne ke liye
        const records =
          response.labtests ||
          response.tests ||
          response.records ||
          response.data ||
          [];
        setRadiologyGroupedData(groupByExamType(records, "radiology"));
        setLaboratoryGroupedData(groupByExamType(records, "laboratory"));
      } catch (error) {
        toast.error("Error fetching lab data");
      }
    };
    fetchLabData();
  }, []);

  useEffect(() => {
    const q = searchData.toLowerCase();
    setPaginatedMedicinesData(
      medicinesGroupedData.filter((item) => (item.invoice || "").toLowerCase().includes(q))
    );
    setPaginatedServicesData(
      servicesGroupedData.filter((item) => (item.invoice || "").toLowerCase().includes(q))
    );
    setPaginatedPatientData(
      patientGroupedData.filter((item) =>
        String(item.patient_id || "").toLowerCase().includes(q)
      )
    );
  }, [searchData, medicinesGroupedData, servicesGroupedData, patientGroupedData]);

  /* ---------- combine ---------- */

  const filterDataByMonth = (data) =>
    data.filter(
      (item) =>
        new Date(item.date).getMonth() + 1 === Number(selectedMonth) &&
        new Date(item.date).getFullYear() === Number(selectedYear)
    );

  const combinedData = filterDataByMonth(paginatedServicesData).map((serviceGroup) => {
    const medicinesData = paginatedMedicinesData.find((m) => m.date === serviceGroup.date) || {};
    const patientData = paginatedPatientData.find((p) => p.date === serviceGroup.date) || {};
    const radiologyData = radiologyGroupedData.find((r) => r.date === serviceGroup.date) || {};
    const laboratoryData = laboratoryGroupedData.find((l) => l.date === serviceGroup.date) || {};

    return {
      date: serviceGroup.date,
      patientSales: patientData.patientCount || 0,
      patientAmount: patientData.totalAmount || 0,
      medicinesSales: medicinesData.totalInvoiceCount || 0,
      medicinesAmount: medicinesData.totalAmount || 0,
      servicesSales: serviceGroup.totalInvoiceCount || 0,
      servicesAmount: serviceGroup.totalAmount || 0,
      radiologySales: radiologyData.orderCount || 0,
      radiologyAmount: radiologyData.totalAmount || 0,
      laboratorySales: laboratoryData.orderCount || 0,
      laboratoryAmount: laboratoryData.totalAmount || 0,
    };
  });

  const getRowTotal = (item) =>
    Number(item.patientAmount) +
    Number(item.medicinesAmount) +
    Number(item.servicesAmount) +
    Number(item.radiologyAmount) +
    Number(item.laboratoryAmount);

  const grandTotal = combinedData.reduce((sum, item) => sum + getRowTotal(item), 0);

  /* ---------- UI ---------- */

  return (
    <div className="p-2">
      <div className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white border border-gray-200 rounded-xl px-4 sm:px-6 py-4 mb-5 shadow-sm gap-3">
          <div>
            <h1 className="text-[17px] font-[500] text-gray-800">Income Summary</h1>
            <p className="text-[12px] text-gray-400 mt-0.5">
              {combinedData.length} records found
            </p>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <select
              className="flex-1 sm:flex-none text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] focus:outline-none text-gray-700"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {months.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
            <select
              className="flex-1 sm:flex-none text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] focus:outline-none text-gray-700"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value={2024}>2024</option>
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
            </select>
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse shadow-md rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-[#3173be] text-[11px] uppercase text-white text-center">
                <th className="py-3 px-4 text-left" rowSpan={2}>Date</th>
                <th className="py-2 px-4 border-l border-gray-600" colSpan={2}>Patient</th>
                <th className="py-2 px-4 border-l border-gray-600" colSpan={2}>Medicines</th>
                <th className="py-2 px-4 border-l border-gray-600" colSpan={2}>Services</th>
                <th className="py-2 px-4 border-l border-gray-600" colSpan={2}>Radiology</th>
                <th className="py-2 px-4 border-l border-gray-600" colSpan={2}>Laboratory</th>
                <th className="py-2 px-4 border-l border-gray-600" rowSpan={2}>Total Amount</th>
              </tr>
              <tr className="bg-[#5092dd] text-[11px] uppercase text-gray-100 text-center">
                <th className="py-2 px-4 border-l border-gray-600">Sales</th>
                <th className="py-2 px-4">Amount</th>
                <th className="py-2 px-4 border-l border-gray-600">Sales</th>
                <th className="py-2 px-4">Amount</th>
                <th className="py-2 px-4 border-l border-gray-600">Sales</th>
                <th className="py-2 px-4">Amount</th>
                <th className="py-2 px-4 border-l border-gray-600">Sales</th>
                <th className="py-2 px-4">Amount</th>
                <th className="py-2 px-4 border-l border-gray-600">Sales</th>
                <th className="py-2 px-4">Amount</th>
              </tr>
            </thead>
            <tbody>
              {combinedData.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-16 text-gray-400 text-sm bg-white">
                    No records found for this period.
                  </td>
                </tr>
              ) : (
                combinedData.map((item, index) => (
                  <tr
                    key={item.date}
                    className={`border-t text-[13px] hover:bg-gray-50 ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="py-2 px-4 font-[500] text-gray-800 whitespace-nowrap">{item.date}</td>
                    <td className="py-2 px-4 text-center">{item.patientSales}</td>
                    <td className="py-2 px-4 text-right">{item.patientAmount}</td>
                    <td className="py-2 px-4 text-center">{item.medicinesSales}</td>
                    <td className="py-2 px-4 text-right">{item.medicinesAmount}</td>
                    <td className="py-2 px-4 text-center">{item.servicesSales}</td>
                    <td className="py-2 px-4 text-right">{item.servicesAmount}</td>
                    <td className="py-2 px-4 text-center">{item.radiologySales}</td>
                    <td className="py-2 px-4 text-right">{item.radiologyAmount}</td>
                    <td className="py-2 px-4 text-center">{item.laboratorySales}</td>
                    <td className="py-2 px-4 text-right">{item.laboratoryAmount}</td>
                    <td className="py-2 px-4 text-right font-semibold text-gray-800">
                      {getRowTotal(item).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden flex flex-col gap-3">
          {combinedData.map((item) => (
            <div
              key={item.date}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
            >
              <div className="bg-[#2F6DB4] text-white text-[13px] font-medium px-4 py-2">
                {item.date}
              </div>

              <div className="grid grid-cols-2 gap-px bg-gray-100">
                {[
                  ["Patient", item.patientSales, item.patientAmount],
                  ["Medicines", item.medicinesSales, item.medicinesAmount],
                  ["Services", item.servicesSales, item.servicesAmount],
                  ["Radiology", item.radiologySales, item.radiologyAmount],
                  ["Laboratory", item.laboratorySales, item.laboratoryAmount],
                ].map(([label, sales, amount]) => (
                  <div key={label} className="bg-white px-3 py-3">
                    <p className="text-[10px] uppercase text-gray-400 mb-1">{label}</p>
                    <p className="text-[13px] font-medium text-gray-800">{sales} sales</p>
                    <p className="text-[12px] text-gray-500 mt-0.5">{amount}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 border-t border-gray-100 px-4 py-2 flex justify-between items-center">
                <span className="text-[11px] uppercase text-gray-400">Total amount</span>
                <span className="text-[14px] font-semibold text-[#2F6DB4]">
                  {getRowTotal(item).toFixed(2)}
                </span>
              </div>
            </div>
          ))}

          {combinedData.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex justify-between items-center">
              <span className="text-[12px] uppercase text-blue-500 font-medium">Grand total</span>
              <span className="text-[15px] font-bold text-[#2F6DB4]">{grandTotal.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Income;